import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";

import {
  formatOpeningDate,
  formatSnapshotDate,
  formatStars,
  getAddressLines,
  getHealthScoreDisplay,
  getPostPhotoUrl,
  getPrimaryPhone,
  getRegularHoursByDay,
  SNAPSHOT_TRANSLATION_PREFIX,
  SOCIAL_PLATFORMS,
} from "./utils";
import {
  FacilityReportFacilitySnapshotData,
  FacilitySnapshotPost,
  FacilitySnapshotReview,
} from "../../../../models/FacilityReportData";

import "./styles.scss";

interface IFacilitySnapshotSectionProps {
  snapshot?: FacilityReportFacilitySnapshotData;
}

export const FacilitySnapshotSection: React.FunctionComponent<
  IFacilitySnapshotSectionProps
> = ({ snapshot }) => {
  const { t: _t } = useTranslation();
  const t = (key: string, options?: Record<string, string | number>) =>
    _t(`${SNAPSHOT_TRANSLATION_PREFIX}.${key}`, options);

  // Reports generated without the facility snapshot option have no snapshot
  if (!snapshot) {
    return null;
  }

  const { reviews, local_posts: localPosts } = snapshot;
  const healthScore = snapshot.health_index
    ? getHealthScoreDisplay(snapshot.health_index)
    : undefined;
  const addressLines = getAddressLines(snapshot, t("postal_prefix"));
  const phone = getPrimaryPhone(snapshot);
  const openingDate = formatOpeningDate(snapshot, t);
  const regularHours = getRegularHoursByDay(snapshot);

  const renderReview = (review: FacilitySnapshotReview, index: number) => (
    <div className="snapshot-review-card" key={index}>
      <div className="snapshot-review-header">
        <span className="snapshot-review-name">
          {review.reviewer_name || t("no_name")}
        </span>
        <span className="snapshot-muted">
          {formatSnapshotDate(review.review_date)}
        </span>
      </div>
      <div className="snapshot-stars">{formatStars(review.rating)}</div>
      <p className="snapshot-review-comment">
        {review.comment || t("rating_only")}
      </p>
    </div>
  );

  const renderPost = (post: FacilitySnapshotPost, index: number) => {
    const photoUrl = getPostPhotoUrl(post);
    return (
      <div className="snapshot-post-card" key={index}>
        <div className="snapshot-post-image">
          {photoUrl ? (
            <img src={photoUrl} alt="" referrerPolicy="no-referrer" />
          ) : (
            <span>{t("post_image_placeholder")}</span>
          )}
        </div>
        <div className="snapshot-post-body">
          <span className="snapshot-muted">
            {formatSnapshotDate(post.post_date)}
          </span>
          <p className="snapshot-post-summary">{post.summary}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="facility-snapshot-section white-border-panel facility-report-details-section">
      <div
        className="inner-container"
        id="facility-report-details-facility-snapshot-section"
      >
        <div className="snapshot-card snapshot-header">
          <div>
            <h2 className="snapshot-facility-name">{snapshot.name}</h2>
            <div className="snapshot-muted">
              <span>{t("facility_id", { id: snapshot.location_id })}</span>
              <span>
                {t("snapshot_date", {
                  date: dayjs(snapshot.snapshot_date).format(
                    t("datetime_format")
                  ),
                })}
              </span>
            </div>
          </div>
          {healthScore && (
            <div className="snapshot-health-index">
              <span className="snapshot-muted">{t("health_index")}</span>
              <span
                className={`snapshot-health-score ${healthScore.className}`}
              >
                {healthScore.label}
              </span>
            </div>
          )}
        </div>

        <div className="snapshot-body">
          <div className="snapshot-card snapshot-profile">
            {snapshot.main_category && (
              <div className="snapshot-field">
                <span className="snapshot-chip">{t("main_category")}</span>
                <strong>{snapshot.main_category.name}</strong>
              </div>
            )}
            {snapshot.sub_categories.length > 0 && (
              <div className="snapshot-field">
                <span className="snapshot-chip">{t("sub_categories")}</span>
                {snapshot.sub_categories.map((category) => (
                  <span key={category.gbp_category_id}>{category.name}</span>
                ))}
              </div>
            )}

            <div className="snapshot-divider" />

            <div className="snapshot-field">
              <span className="snapshot-label">{t("address")}</span>
              {addressLines.length > 0 ? (
                addressLines.map((line) => <span key={line}>{line}</span>)
              ) : (
                <span className="snapshot-empty">{t("not_registered")}</span>
              )}
            </div>
            <div className="snapshot-field">
              <span className="snapshot-label">{t("phone")}</span>
              <span className={phone ? "" : "snapshot-empty"}>
                {phone || t("not_registered")}
              </span>
            </div>
            <div className="snapshot-field">
              <span className="snapshot-label">{t("website")}</span>
              {snapshot.website_url ? (
                <a
                  href={snapshot.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {snapshot.website_url}
                </a>
              ) : (
                <span className="snapshot-empty">{t("not_registered")}</span>
              )}
            </div>
            <div className="snapshot-field">
              <span className="snapshot-label">{t("opening_date")}</span>
              <span className={openingDate ? "" : "snapshot-empty"}>
                {openingDate || t("not_registered")}
              </span>
            </div>
            <div className="snapshot-field">
              <span className="snapshot-label">{t("regular_hours")}</span>
              {regularHours ? (
                <div className="snapshot-hours">
                  {regularHours.map(({ day, periods }) => (
                    <div className="snapshot-hours-row" key={day}>
                      <span>{t(`days.${day}`)}</span>
                      <span>
                        {periods.length > 0
                          ? periods.join(", ")
                          : t("closed")}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="snapshot-empty">{t("not_registered")}</span>
              )}
            </div>
            <div className="snapshot-field">
              <span className="snapshot-label">{t("social_profiles")}</span>
              <div className="snapshot-social-icons">
                {SOCIAL_PLATFORMS.map(({ key, label, color }) => {
                  const url = snapshot.social_profiles?.[key]?.url;
                  return url ? (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="snapshot-social-icon linked"
                      style={{ backgroundColor: color, borderColor: color }}
                    >
                      {label}
                    </a>
                  ) : (
                    <span key={key} className="snapshot-social-icon">
                      {label}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="snapshot-main">
            <div className="snapshot-card">
              <div className="snapshot-card-header">
                <h3>
                  {t("recent_reviews", { count: reviews.recent_reviews.length })}
                </h3>
                {!!reviews.total_review_count && (
                  <span className="snapshot-muted">
                    {t("review_totals", {
                      count: reviews.total_review_count,
                      average: (reviews.average_rating ?? 0).toFixed(1),
                    })}
                  </span>
                )}
              </div>
              {reviews.recent_reviews.length > 0 ? (
                <div className="snapshot-reviews">
                  {reviews.recent_reviews.map(renderReview)}
                </div>
              ) : (
                <p className="snapshot-empty">{t("no_reviews")}</p>
              )}
            </div>

            <div className="snapshot-card">
              <div className="snapshot-card-header">
                <h3>{t("recent_posts", { count: localPosts.recent_posts.length })}</h3>
                <span className="snapshot-muted">
                  {t("recent_posts_count", {
                    days: localPosts.recent_posts_period_days,
                    count: localPosts.recent_posts_count,
                  })}
                </span>
              </div>
              {localPosts.recent_posts.length > 0 ? (
                <div className="snapshot-posts">
                  {localPosts.recent_posts.map(renderPost)}
                </div>
              ) : (
                <p className="snapshot-empty">{t("no_posts")}</p>
              )}
            </div>

            <div className="snapshot-card snapshot-description">
              <h3>{t("description")}</h3>
              {snapshot.profile ? (
                <p className="snapshot-description-text">{snapshot.profile}</p>
              ) : (
                <p className="snapshot-empty">{t("not_registered")}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
